<script setup lang="ts">
  import type { Collections } from '@nuxt/content'
  import gsap from 'gsap'
  import { ScrollTrigger } from 'gsap/ScrollTrigger'
  import { SplitText } from 'gsap/SplitText'

  const root = ref<HTMLElement>()
  let media: gsap.MatchMedia | undefined

  onMounted(() => {
    gsap.registerPlugin(ScrollTrigger, SplitText)
    media = gsap.matchMedia()

    media.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(root.value)
      root.value!.classList.add('fiche-ready')

      // Header: the name rises letter by letter, then the facts arrive.
      const title = SplitText.create(q('.fiche-title')[0], { type: 'words,chars', mask: 'chars' })
      gsap.timeline({ defaults: { ease: 'expo.out', lazy: false } })
        .from(title.chars, { yPercent: 110, duration: 1, stagger: 0.025 }, 0)
        .from(q('.fiche-intro > *'), { y: 16, opacity: 0, duration: 0.7, stagger: 0.08, ease: 'power3.out' }, 0.4)

      // The visual opens on arrival, grows with the scroll, and the phone drifts in front.
      gsap.timeline({ defaults: { ease: 'power3.out', lazy: false } })
        .from(q('.fiche-browser'), { clipPath: 'inset(14% 14% 14% 14% round 12px)', opacity: 0, duration: 1.2, ease: 'expo.out', clearProps: 'clipPath,opacity' }, 0.5)
        .from(q('.fiche-phone-in'), { y: 70, opacity: 0, duration: 1, clearProps: 'transform,opacity' }, 0.8)
      gsap.fromTo(q('.fiche-stage'), { scale: 0.94 }, { scale: 1, ease: 'none', scrollTrigger: { trigger: q('.fiche-stage')[0], start: 'top 85%', end: 'top 15%', scrub: 0.5 } })
      gsap.fromTo(q('.fiche-phone'), { y: 40 }, { y: -60, ease: 'none', scrollTrigger: { trigger: q('.fiche-stage')[0], start: 'top bottom', end: 'bottom top', scrub: 0.6 } })

      // The description lights up word by word while it is read.
      const lead = q('.fiche-lead')[0]
      const words = lead ? SplitText.create(lead, { type: 'words' }) : undefined
      if (words) {
        gsap.fromTo(words.words, { opacity: 0.18 }, {
          opacity: 1,
          stagger: 0.1,
          ease: 'none',
          scrollTrigger: { trigger: lead, start: 'top 78%', end: 'bottom 55%', scrub: 0.4 },
        })
      }

      q('.fiche-reveal').forEach((element) => {
        gsap.from(element, { y: 28, opacity: 0, duration: 0.8, ease: 'power3.out', scrollTrigger: { trigger: element, start: 'top 88%', once: true } })
      })

      return () => {
        title.revert()
        words?.revert()
        root.value?.classList.remove('fiche-ready')
      }
    }, root.value)
  })

  onBeforeUnmount(() => media?.revert())

  const route = useRoute()
  const { locale, t } = useI18n()
  const localePath = useLocalePath()
  const { seo, profile } = useAppConfig()
  const slug = route.params.slug as string

  const { data: project } = await useAsyncData(`project-${slug}-${locale.value}`, async () => {
    const collection = ('projects_' + locale.value) as keyof Collections
    const allProjects = await queryCollection(collection).all()
    return allProjects.find(p => p.stem.endsWith(`/${slug}`)) as Collections['projects_fr']
  }, {
    watch: [locale],
  })

  const { data: allProjects } = await useAsyncData(`projects-${locale.value}`, async () => {
    const collection = ('projects_' + locale.value) as keyof Collections
    return await queryCollection(collection).all() as Collections['projects_en'][] | Collections['projects_fr'][]
  }, {
    watch: [locale],
  })

  // The following project, looping back to the first one after the last.
  const nextProject = computed<any>(() => {
    const list = allProjects.value || []
    const index = list.findIndex(p => p.stem === project.value?.stem)
    return list.length > 1 ? list[(index + 1) % list.length] : undefined
  })

  const isSoon = computed(() => /soon|bient/i.test(project.value?.release || ''))
  const summary = computed(() => project.value?.summary || project.value?.description || '')
  const publishedDate = computed(() => {
    if (isSoon.value) return undefined
    if (project.value?.date) return project.value.date
    const yearMatch = project.value?.release?.match(/\d{4}/)?.[0]
    return yearMatch ? `${yearMatch}-01-01` : undefined
  })

  if (!project.value) {
    throw createError({ statusCode: 404, statusMessage: 'Project not found' })
  }

  const url = useRequestURL()
  const canonicalUrl = computed(() => {
    const path = url.pathname.endsWith('/') && url.pathname !== '/' ? url.pathname.slice(0, -1) : url.pathname
    return `${seo.url}${path}`
  })

  useSeoMeta({
    title: project.value.name,
    description: summary.value,
    ogSiteName: seo.title,
    ogTitle: project.value.name,
    ogDescription: summary.value,
    ogImage: project.value.image,
    ogUrl: canonicalUrl.value,
    author: profile.name,
    twitterTitle: project.value.name,
    twitterDescription: summary.value,
    twitterCard: 'summary_large_image',
  })

  useHead({
    link: [
      { rel: 'canonical', href: canonicalUrl.value },
    ],
  })

  useSchemaOrg([
    defineWebPage({
      '@type': 'CreativeWork' as any,
      name: project.value.name,
      description: summary.value,
      image: project.value.image,
      datePublished: publishedDate.value,
      url: canonicalUrl.value,
    }),
    defineBreadcrumb({
      itemListElement: [
        { name: t('navigation.home'), item: '/' },
        { name: t('navigation.works'), item: '/works' },
        { name: project.value.name, item: route.path },
      ],
    }),
  ])
</script>

<template>
  <div
    v-if="project"
    ref="root"
    class="fiche-root mx-auto max-w-7xl overflow-x-clip px-4 pb-16 sm:px-6 lg:px-8"
  >
    <NuxtLink
      :to="localePath('/works')"
      class="group mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/60 transition-colors hover:text-white sm:mt-16"
    >
      <UIcon
        name="i-heroicons-arrow-left"
        class="size-4 transition-transform group-hover:-translate-x-1"
      />
      {{ t('global.all_projects') }}
    </NuxtLink>

    <article>
      <!-- header -->
      <header class="mt-8">
        <h1 class="fiche-in fiche-title text-[clamp(3.25rem,11vw,10rem)] font-semibold leading-[0.98] tracking-tighter text-white">
          {{ project.name }}
        </h1>
        <div class="fiche-in fiche-intro mt-10 grid gap-x-8 gap-y-6 border-t border-white/15 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <div v-if="project.industry">
            <p class="text-sm text-white/50">
              {{ t('global.project_industry') }}
            </p>
            <p class="mt-1 font-medium text-white">
              {{ project.industry }}
            </p>
          </div>
          <div>
            <p class="text-sm text-white/50">
              {{ t('global.project_year') }}
            </p>
            <p class="mt-1 font-medium text-white">
              <time :datetime="publishedDate">{{ isSoon ? t('global.soon') : project.release }}</time>
            </p>
          </div>
          <div v-if="project.role">
            <p class="text-sm text-white/50">
              {{ t('global.project_role') }}
            </p>
            <p class="mt-1 font-medium text-white">
              {{ project.role }}
            </p>
          </div>
          <a
            v-if="project.link"
            :href="project.link"
            target="_blank"
            rel="noopener"
            class="group flex h-14 w-fit items-center gap-4 self-start rounded-full bg-white pl-6 pr-2 font-medium text-neutral-950 lg:col-start-4 lg:justify-self-end"
          >
            {{ t('global.project_visit_site') }}
            <span class="flex size-10 items-center justify-center rounded-full bg-neutral-950 text-white">
              <UIcon
                name="i-heroicons-arrow-up-right"
                class="size-4 transition-transform duration-300 group-hover:rotate-45"
              />
            </span>
          </a>
        </div>
      </header>

      <!-- the project on a large screen and on a phone -->
      <div class="fiche-stage relative mt-16 pr-[9%] lg:mt-24">
        <div class="fiche-in fiche-browser overflow-hidden rounded-xl border border-white/10 bg-neutral-900 shadow-2xl shadow-black/60">
          <div class="flex h-9 items-center gap-1.5 border-b border-white/10 px-4">
            <span
              v-for="dot in 3"
              :key="dot"
              class="size-2.5 rounded-full bg-white/20"
            />
          </div>
          <div class="aspect-video">
            <NuxtImg
              :src="project.body?.banner ?? project.image"
              width="1600"
              densities="x1"
              :alt="project.name"
              class="size-full object-cover object-top"
            />
          </div>
        </div>
        <div class="fiche-phone absolute bottom-[-7%] right-0 w-[23%]">
          <ProjectPhone class="fiche-in fiche-phone-in w-full">
            <NuxtImg
              :src="project.body?.phone ?? project.image"
              width="640"
              densities="x1"
              alt=""
              class="size-full object-cover object-top"
            />
          </ProjectPhone>
        </div>
      </div>

      <!-- the story -->
      <p
        v-if="project.description"
        class="fiche-lead mt-28 max-w-5xl text-pretty text-3xl font-medium leading-[1.25] tracking-tight text-white sm:text-4xl lg:mt-44 lg:text-5xl"
      >
        {{ project.description }}
      </p>

      <div
        v-if="project.about_client || project.about_project"
        class="mt-20 grid gap-12 lg:mt-28 lg:grid-cols-2 lg:gap-16"
      >
        <section
          v-if="project.about_client"
          class="fiche-reveal"
        >
          <h2 class="text-sm text-white/50">
            {{ t('global.about_client') }}
          </h2>
          <p class="mt-3 text-pretty text-lg leading-relaxed text-white/80">
            {{ project.about_client }}
          </p>
        </section>
        <section
          v-if="project.about_project"
          class="fiche-reveal"
        >
          <h2 class="text-sm text-white/50">
            {{ t('global.about_project') }}
          </h2>
          <p class="mt-3 text-pretty text-lg leading-relaxed text-white/80">
            {{ project.about_project }}
          </p>
        </section>
      </div>

      <!-- highlights -->
      <section
        v-if="project.highlights?.length"
        class="mt-24 grid gap-8 lg:mt-36 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-16"
      >
        <h2 class="fiche-reveal text-4xl font-semibold tracking-tight text-white sm:text-5xl">
          {{ t('global.project_highlights') }}
        </h2>
        <ol class="border-t border-white/10">
          <li
            v-for="(item, index) in project.highlights"
            :key="item"
            class="fiche-reveal flex items-baseline gap-6 border-b border-white/10 py-5 sm:py-6"
          >
            <span class="text-sm tabular-nums text-white/40">{{ String(index + 1).padStart(2, '0') }}</span>
            <span class="text-pretty text-lg font-medium text-white sm:text-xl">{{ item }}</span>
          </li>
        </ol>
      </section>

      <!-- services and results -->
      <div
        v-if="project.services?.length || project.results?.length"
        class="mt-20 grid gap-12 lg:mt-28 lg:grid-cols-2 lg:gap-16"
      >
        <section
          v-if="project.services?.length"
          class="fiche-reveal"
        >
          <h2 class="text-sm text-white/50">
            {{ t('global.project_services') }}
          </h2>
          <ul class="mt-4 flex flex-wrap gap-2">
            <li
              v-for="service in project.services"
              :key="service"
              class="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-white/80"
            >
              {{ service }}
            </li>
          </ul>
        </section>
        <section
          v-if="project.results?.length"
          class="fiche-reveal"
        >
          <h2 class="text-sm text-white/50">
            {{ t('global.project_results') }}
          </h2>
          <ul class="mt-4 space-y-2 text-lg text-white/80">
            <li
              v-for="item in project.results"
              :key="item"
            >
              {{ item }}
            </li>
          </ul>
        </section>
      </div>

      <!-- gallery, only when a project has more than its main image -->
      <section
        v-if="project.gallery?.length > 1"
        class="mt-24 space-y-8"
      >
        <h2 class="fiche-reveal text-4xl font-semibold tracking-tight text-white sm:text-5xl">
          {{ t('global.project_gallery') }}
        </h2>
        <div
          v-for="(img, idx) in project.gallery.filter((i: string) => i !== project!.image)"
          :key="idx"
          class="fiche-reveal overflow-hidden rounded-xl border border-white/10"
        >
          <NuxtImg
            :src="img"
            :alt="`${project.name} ${idx + 1}`"
            class="h-auto w-full object-cover"
            loading="lazy"
          />
        </div>
      </section>
    </article>

    <!-- next project -->
    <NuxtLink
      v-if="nextProject"
      :to="localePath('/projects/' + nextProject.stem.split('/').pop())"
      class="fiche-reveal group mt-28 block border-t border-white/15 pt-8 lg:mt-40"
    >
      <span class="text-sm text-white/50">{{ t('global.project_following') }}</span>
      <span class="mt-3 flex items-center justify-between gap-6">
        <span class="text-balance text-[clamp(2.75rem,8vw,7rem)] font-semibold leading-[1] tracking-tighter text-white">
          {{ nextProject.name }}
        </span>
        <span class="flex size-14 shrink-0 items-center justify-center rounded-full bg-white text-neutral-950 transition-transform duration-500 group-hover:translate-x-1 group-hover:-rotate-45 sm:size-20">
          <UIcon
            name="i-heroicons-arrow-right"
            class="size-6 sm:size-8"
          />
        </span>
      </span>
    </NuxtLink>
  </div>
</template>

<style>
  /* Hidden until the entrance starts, with a fallback in case the script never runs. */
  @media (prefers-reduced-motion: no-preference) {
    .fiche-root:not(.fiche-ready) .fiche-in {
      opacity: 0;
      animation: fiche-fallback 0.3s 2.5s forwards;
    }
  }

  @keyframes fiche-fallback {
    to {
      opacity: 1;
    }
  }
</style>
